import React from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import Navbar from "./components/Navbar";
import HomePage from "./pages/HomePage";
import InventoryPage from "./pages/InventoryPage";
import VehiclePage from "./pages/VehiclePage";
import VehicleRegistryPage from "./pages/VehicleRegistryPage";
import SupplierPage from "./pages/SupplierPage";
import CustomerPage from "./pages/CustomerPage";
import POSPage from "./pages/POSPage";
import SalesHistoryPage from "./pages/SalesHistoryPage";
import LoginPage from "./pages/LoginPage";
import EmployeePage from "./pages/EmployeePage";
import DailyReportPage from "./pages/DailyReportPage";
import InsightsPage from "./pages/InsightsPage";
import OptionsPage from "./pages/OptionsPage";
import EstimatePage from "./pages/EstimatePage";
import EstimateListPage from "./pages/EstimateListPage";
import PurchaseOrdersPage from "./pages/PurchaseOrdersPage";
import SupplierPaymentsPage from "./pages/SupplierPaymentsPage";
import InspectionPage from "./pages/InspectionPage";
import InspectionListPage from "./pages/InspectionListPage";
import RepairServicesPage from "./pages/RepairServicesPage";
import PrivateRoute from "./components/PrivateRoute";
import useAutoLogout from "./hooks/useAutoLogout";
import { PartsProvider } from "./context/PartsContext";
import { SettingsProvider } from "./context/SettingsContext";

// Helper component to hide Navbar on Login page
const Layout = ({ children }) => {
  const location = useLocation();
  // Don't show Navbar if we are on /login
  if (location.pathname === "/login") {
    return children;
  }
  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <Navbar />
      <div className="container mx-auto">{children}</div>
    </div>
  );
};

// The whole app lives under one catch-all route of a data router. The routes
// themselves stay declared as <Routes> below; the data router is only there so
// pages can use useBlocker (see useUnsavedChangesPrompt).
const AppRoutes = () => (
  <SettingsProvider>
  <PartsProvider>
    <Layout>
    <Routes>
      {/* Public Route */}
      <Route path="/login" element={<LoginPage />} />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <PrivateRoute>
            <HomePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/pos"
        element={
          <PrivateRoute>
            <POSPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <PrivateRoute>
            <InventoryPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/suppliers"
        element={
          <PrivateRoute>
            <SupplierPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <PrivateRoute>
            <CustomerPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/vehicles"
        element={
          <PrivateRoute>
            <VehicleRegistryPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/vehicle-models"
        element={
          <PrivateRoute>
            <VehiclePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/repairs"
        element={
          <PrivateRoute>
            <RepairServicesPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/employees"
        element={
          <PrivateRoute>
            <EmployeePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/sales-history"
        element={
          <PrivateRoute>
            <SalesHistoryPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/insights"
        element={
          <PrivateRoute>
            <InsightsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/daily-report"
        element={
          <PrivateRoute>
            <DailyReportPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/options"
        element={
          <PrivateRoute>
            <OptionsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/purchase-orders"
        element={
          <PrivateRoute>
            <PurchaseOrdersPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/supplier-payments"
        element={
          <PrivateRoute>
            <SupplierPaymentsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/estimates"
        element={
          <PrivateRoute>
            <EstimateListPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/estimates/new"
        element={
          <PrivateRoute>
            <EstimatePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/estimates/:id"
        element={
          <PrivateRoute>
            <EstimatePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/inspections"
        element={
          <PrivateRoute>
            <InspectionListPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/inspections/new"
        element={
          <PrivateRoute>
            <InspectionPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/inspections/:id"
        element={
          <PrivateRoute>
            <InspectionPage />
          </PrivateRoute>
        }
      />
    </Routes>
    </Layout>
  </PartsProvider>
  </SettingsProvider>
);

const router = createBrowserRouter([{ path: "*", element: <AppRoutes /> }]);

function App() {
  useAutoLogout();
  return <RouterProvider router={router} />;
}

export default App;
